import { useCallback, useEffect, useRef, useState } from "react";
import { Api } from "services/api/api.service";
import type { TimeReportV2Detail } from "services/api/company/timeReports.service";

interface TimeReportDetailsInput {
  readonly companyId?: number | null;
  readonly timeReportId: number | null;
  readonly updatedAt?: string;
}

type TimeReportDetailsState =
  | { readonly detail: null; readonly type: "idle" | "error" }
  | { readonly detail: TimeReportV2Detail | null; readonly type: "loading" }
  | { readonly detail: TimeReportV2Detail; readonly type: "loaded" };

interface TimeReportDetailsResult {
  readonly detail: TimeReportV2Detail | null;
  readonly hasError: boolean;
  readonly isLoading: boolean;
  readonly fetchDetails: () => Promise<void>;
}

const emptyState: TimeReportDetailsState = {
  detail: null,
  type: "idle",
};

export const useTimeReportDetails = ({
  companyId,
  timeReportId,
  updatedAt,
}: TimeReportDetailsInput): TimeReportDetailsResult => {
  const [state, setState] = useState<TimeReportDetailsState>(emptyState);
  const latestRequestIdRef = useRef(0);
  const scopeRef = useRef<TimeReportDetailsInput | null>(null);
  scopeRef.current = { companyId, timeReportId };

  const fetchDetails = useCallback(async (): Promise<void> => {
    const scope = scopeRef.current;
    if (
      !scope ||
      scope.companyId !== companyId ||
      scope.timeReportId !== timeReportId
    )
      return;
    const requestId = ++latestRequestIdRef.current;
    if (!companyId || !timeReportId) {
      setState(emptyState);
      return;
    }

    setState((current) => ({ detail: current.detail, type: "loading" }));
    try {
      const response = await Api().company.timeReports.getTimeReportV2(
        companyId,
        timeReportId,
      );
      if (requestId !== latestRequestIdRef.current) return;
      if (
        response.data.userNotes != null &&
        typeof response.data.userNotes !== "string"
      ) {
        setState({ detail: null, type: "error" });
        return;
      }
      setState({ detail: response.data, type: "loaded" });
    } catch {
      if (requestId !== latestRequestIdRef.current) return;
      setState({ detail: null, type: "error" });
    }
  }, [companyId, timeReportId]);

  useEffect(() => {
    setState(emptyState);
  }, [companyId, timeReportId]);

  useEffect(() => {
    scopeRef.current = { companyId, timeReportId };
    void fetchDetails();
    return () => {
      scopeRef.current = null;
      latestRequestIdRef.current += 1;
    };
  }, [companyId, timeReportId, fetchDetails, updatedAt]);

  return {
    detail: state.detail,
    hasError: state.type === "error",
    isLoading: state.type === "loading",
    fetchDetails,
  };
};
